import IonToolbar from '../../../../../components/Modal/ModalToolbar';
import { IonButton, IonButtons, IonCol, IonContent, IonFooter, IonGrid, IonHeader, IonIcon, IonInput, IonLabel, IonRow, IonSegment, IonSegmentButton, IonText, IonTitle } from '@ionic/react';
import { arrowBack } from 'ionicons/icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import "./Desconto.css";


type TipoDescontoTela = 'valor' | 'porcentagem';

export type DescontoPayload = {
  tipo: 'VALOR' | 'PORCENTAGEM';
  valor: number;
};

export type DescontoValorInicial = DescontoPayload | number | string;

type Props = {
  projetoid: string;
  totalBase?: number | string;
  valorInicial?: DescontoValorInicial;
  modalAberto?: boolean;
  closeAction: (desconto: DescontoPayload) => void | Promise<void>;
  closeActionSimple: () => void;
};

const VALORES_RAPIDOS: Record<TipoDescontoTela, number[]> = {
  valor: [5, 10, 25, 50, 100],
  porcentagem: [5, 10, 15, 20, 50],
};

const formatarMoeda = (valor: number) => valor.toLocaleString('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const normalizarTotalBase = (valor?: number | string) => {
  if (typeof valor === 'number') {
    return Number.isFinite(valor) ? valor : 0;
  }

  if (typeof valor === 'string') {
    const totalNormalizado = Number.parseFloat(valor.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(totalNormalizado) ? totalNormalizado : 0;
  }

  return 0;
};

const limitarPorcentagem = (valor: number) => Math.max(0, Math.min(100, Math.round(valor)));

const formatarInputValor = (valor: number, editando: boolean) => {
  if (!valor) {
    return editando ? '' : '0,00';
  }

  if (editando) {
    return valor.toLocaleString('pt-BR', {
      useGrouping: false,
      minimumFractionDigits: Number.isInteger(valor) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  }

  return formatarMoeda(valor);
};

const formatarInputPorcentagem = (valor: number, editando: boolean) => {
  const valorLimitado = limitarPorcentagem(valor);

  if (!valorLimitado && editando) {
    return '';
  }

  return String(valorLimitado);
};

export const calcularValorDesconto = (desconto: DescontoPayload, totalBase?: number | string) => {
  if (desconto.tipo === 'PORCENTAGEM') {
    const total = normalizarTotalBase(totalBase);
    const percentual = limitarPorcentagem(desconto.valor);
    return Number(((total * percentual) / 100).toFixed(2));
  }

  return Number(Math.max(desconto.valor, 0).toFixed(2));
};

export const converterDescontoParaValorMonetario = (desconto: DescontoPayload, totalBase?: number | string) => {
  return formatarMoeda(calcularValorDesconto(desconto, totalBase));
};

const normalizarDescontoPayload = (valorInicial?: DescontoValorInicial): DescontoPayload => {
  if (typeof valorInicial === 'object' && valorInicial !== null && 'tipo' in valorInicial) {
    if (valorInicial.tipo === 'PORCENTAGEM') {
      return {
        tipo: 'PORCENTAGEM',
        valor: limitarPorcentagem(Number(valorInicial.valor) || 0),
      };
    }

    return {
      tipo: 'VALOR',
      valor: Number(Math.max(Number(valorInicial.valor) || 0, 0).toFixed(2)),
    };
  }

  return {
    tipo: 'VALOR',
    valor: Number(Math.max(normalizarTotalBase(valorInicial), 0).toFixed(2)),
  };
};

export const resolverDescontoInicial = (
  valorInicial?: DescontoValorInicial,
  totalBase?: number | string,
  descontoAtual?: DescontoPayload
) => {
  const valorInicialVazio = valorInicial === undefined
    || valorInicial === null
    || (typeof valorInicial === 'string' && valorInicial.trim() === '');

  if (descontoAtual && valorInicialVazio) {
    return descontoAtual;
  }

  const descontoNormalizado = normalizarDescontoPayload(valorInicial);

  if (!descontoAtual) {
    return descontoNormalizado;
  }

  if (descontoAtual.tipo === 'PORCENTAGEM' && normalizarTotalBase(totalBase) <= 0) {
    return descontoAtual;
  }

  const valorPersistido = calcularValorDesconto(descontoNormalizado, totalBase);
  const valorAtual = calcularValorDesconto(descontoAtual, totalBase);

  if (Math.abs(valorAtual - valorPersistido) < 0.01) {
    return descontoAtual;
  }

  return descontoNormalizado;
};

const Desconto: React.FC<Props> = (props: Props) => {

  const [tipoDesconto, setTipoDesconto] = useState<TipoDescontoTela>('valor');
  const [valorDesconto, setValorDesconto] = useState<number>(0);
  const [inputDesconto, setInputDesconto] = useState('0,00');
  const sincronizandoTipoRef = useRef(false);

  const valoresRapidos = useMemo(() => VALORES_RAPIDOS[tipoDesconto], [tipoDesconto]);

  const previewDesconto = useMemo(() => {
    if (tipoDesconto !== 'porcentagem') {
      return 0;
    }

    return calcularValorDesconto({ tipo: 'PORCENTAGEM', valor: valorDesconto }, props.totalBase);
  }, [props.totalBase, tipoDesconto, valorDesconto]);

  useEffect(() => {
    if (props.modalAberto === false) {
      sincronizandoTipoRef.current = false;
      return;
    }

    sincronizandoTipoRef.current = true;

    const descontoInicial = resolverDescontoInicial(props.valorInicial, props.totalBase);
    const proximoTipo = descontoInicial.tipo === 'VALOR' ? 'valor' : 'porcentagem';

    setTipoDesconto(proximoTipo);
    setValorDesconto(descontoInicial.valor);
    setInputDesconto(
      proximoTipo === 'valor'
        ? formatarInputValor(descontoInicial.valor, false)
        : formatarInputPorcentagem(descontoInicial.valor, false)
    );

    const timeoutId = window.setTimeout(() => {
      sincronizandoTipoRef.current = false;
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      sincronizandoTipoRef.current = false;
    };
  }, [props.modalAberto, props.totalBase, props.valorInicial]);

  const atualizarTipo = (novoTipo: TipoDescontoTela) => {
    if (sincronizandoTipoRef.current || novoTipo === tipoDesconto) {
      return;
    }

    setTipoDesconto(novoTipo);
    setValorDesconto(0);
    setInputDesconto(novoTipo === 'valor' ? '0,00' : '0');
  };

  const selecionarValorRapido = (valor: number) => {
    const proximoValor = tipoDesconto === 'porcentagem' ? limitarPorcentagem(valor) : valor;

    setValorDesconto(proximoValor);
    setInputDesconto(
      tipoDesconto === 'valor'
        ? formatarInputValor(proximoValor, false)
        : formatarInputPorcentagem(proximoValor, false)
    );
  };

  const onFocusInput = () => {
    setInputDesconto(
      tipoDesconto === 'valor'
        ? formatarInputValor(valorDesconto, true)
        : formatarInputPorcentagem(valorDesconto, true)
    );
  };

  const onBlurInput = () => {
    setInputDesconto(
      tipoDesconto === 'valor'
        ? formatarInputValor(valorDesconto, false)
        : formatarInputPorcentagem(valorDesconto, false)
    );
  };

  const atualizarValorMonetario = (valorDigitado: string) => {
    const somenteNumerosEVirgula = valorDigitado.replace(/[^\d,]/g, '');

    if (!somenteNumerosEVirgula) {
      setInputDesconto('');
      setValorDesconto(0);
      return;
    }

    const primeiraVirgula = somenteNumerosEVirgula.indexOf(',');
    const valorSanitizado = primeiraVirgula === -1
      ? somenteNumerosEVirgula
      : `${somenteNumerosEVirgula.slice(0, primeiraVirgula).replace(/,/g, '')},${somenteNumerosEVirgula.slice(primeiraVirgula + 1).replace(/,/g, '').slice(0, 2)}`;

    const [parteInteira = '0', parteDecimal = ''] = valorSanitizado.split(',');
    const inteiroNormalizado = parteInteira.replace(/^0+(?=\d)/, '') || '0';
    const valorNormalizado = parteDecimal.length > 0
      ? `${inteiroNormalizado},${parteDecimal}`
      : inteiroNormalizado;
    const valorNumerico = Number.parseFloat(`${inteiroNormalizado}.${parteDecimal || '0'}`);

    setInputDesconto(valorNormalizado);
    setValorDesconto(Number.isFinite(valorNumerico) ? valorNumerico : 0);
  };

  const atualizarPorcentagem = (valorDigitado: string) => {
    const somenteNumeros = valorDigitado.replace(/\D/g, '').slice(0, 3);

    if (!somenteNumeros) {
      setInputDesconto('');
      setValorDesconto(0);
      return;
    }

    const percentual = limitarPorcentagem(Number(somenteNumeros));
    setInputDesconto(String(percentual));
    setValorDesconto(percentual);
  };

  const obterValorInput = (event: any) => String(event?.target?.value ?? event?.detail?.value ?? '');


  function onChangeValor(event: any) {
    const valorDigitado = obterValorInput(event);

    if (tipoDesconto === 'valor') {
      atualizarValorMonetario(valorDigitado);
      return;
    }

    atualizarPorcentagem(valorDigitado);
  }

  async function salvarDesconto() {
    await props.closeAction({
      tipo: tipoDesconto === 'valor' ? 'VALOR' : 'PORCENTAGEM',
      valor: tipoDesconto === 'valor'
        ? Number(valorDesconto.toFixed(2))
        : limitarPorcentagem(valorDesconto),
    });
  }

  return (

    <>
      <IonHeader className="ion-no-border">
        <IonToolbar color="primary">

          <IonButtons slot="start">
            <IonIcon slot="icon-only" icon={arrowBack} onClick={() => {
              props.closeActionSimple();
            }} />
          </IonButtons>
          <IonTitle>Desconto</IonTitle>

        </IonToolbar>


      </IonHeader>

      <IonContent className="Desconto" fullscreen={false}>

        <IonGrid >
          <IonRow className="ion-justify-content-center" style={{ paddingTop: '20px', textAlign: 'center', fontSize: '20px' }}>
            <IonLabel>Selecione um desconto ou insira manualmente</IonLabel>
          </IonRow>

          <IonRow className="ion-justify-content-center" style={{ paddingTop: '20px' }}>
            <IonSegment
              className="Desconto__segment"
              value={tipoDesconto}
              onIonChange={(event) => atualizarTipo((event.detail.value as TipoDescontoTela) || 'valor')}
            >
              <IonSegmentButton className="Desconto__segmentButton" value="valor">
                <IonLabel>Valor (R$)</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton className="Desconto__segmentButton" value="porcentagem">
                <IonLabel>Porcentagem (%)</IonLabel>
              </IonSegmentButton>
            </IonSegment>
          </IonRow>

          <IonRow className="ion-justify-content-center" style={{ paddingTop: '20px' }}>
            <div className="Desconto__quickButtons">
              {valoresRapidos.map((valor) => (
                <IonButton
                  key={`${tipoDesconto}-${valor}`}
                  className="Desconto__quickButton"
                  color={valorDesconto === valor ? 'primary' : 'light'}
                  onClick={() => selecionarValorRapido(valor)}
                >
                  {tipoDesconto === 'valor' ? formatarMoeda(valor) : `${valor}%`}
                </IonButton>
              ))}
            </div>
          </IonRow>

          <IonRow className="ion-justify-content-center" style={{ paddingTop: '20px', textAlign: 'center', fontSize: '26px' }}>
            <IonCol className="Desconto__inputCol" size="10" sizeSm="8" sizeMd="6">
              <div className="Desconto__inputWrapper">
                <IonInput
                  className={`Desconto__input ${tipoDesconto === 'porcentagem' ? 'Desconto__input--withSuffix' : ''}`}
                  type="tel"
                  inputmode={tipoDesconto === 'valor' ? 'decimal' : 'numeric'}
                  maxlength={tipoDesconto === 'valor' ? 14 : 3}
                  placeholder={tipoDesconto === 'valor' ? '0,00' : '0'}
                  value={inputDesconto}
                  onIonInput={onChangeValor}
                  onIonFocus={onFocusInput}
                  onIonBlur={onBlurInput}
                />
                {tipoDesconto === 'porcentagem' && (
                  <span className="Desconto__inputSuffix">%</span>
                )}
              </div>
            </IonCol>
          </IonRow>

          <IonRow className="ion-justify-content-center" style={{ paddingTop: '8px' }}>
            <IonText className="Desconto__helperText">
              {tipoDesconto === 'valor' ? 'Valor do desconto (R$)' : 'Percentual de desconto (%)'}
            </IonText>
          </IonRow>

          {tipoDesconto === 'porcentagem' && (
            <IonRow className="ion-justify-content-center" style={{ paddingTop: '8px' }}>
              <IonText className="Desconto__previewText">
                Desconto aplicado: R$ {formatarMoeda(previewDesconto)}
              </IonText>
            </IonRow>
          )}
        </IonGrid>
      </IonContent>
      <IonFooter>

        <IonToolbar className="Desconto">
          <IonGrid>

            <IonRow>
              <IonCol size="12">
                <IonButton style={{ height: "46px", fontSize: "18px" }} expand="block"
                  type="submit"
                  onClick={salvarDesconto}
                >
                  Salvar
                </IonButton>
              </IonCol>
            </IonRow>
          </IonGrid>




        </IonToolbar>
      </IonFooter>
    </>


  );
};

const DescontoModal = ({ projetoid, totalBase, valorInicial, modalAberto, closeAction, closeActionSimple }: { projetoid: string, totalBase?: number | string, valorInicial?: DescontoValorInicial, modalAberto?: boolean, closeAction: (desconto: DescontoPayload) => void | Promise<void>, closeActionSimple: () => void }) => (
  <Desconto projetoid={projetoid} totalBase={totalBase} valorInicial={valorInicial} modalAberto={modalAberto} closeAction={closeAction} closeActionSimple={closeActionSimple}>
  </Desconto>
);

export default DescontoModal;
